import React, { useState } from 'react';
import {
  Box, Switch, Button, Autocomplete,
} from '@mui/material';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateIPRestriction,
  updateLocationBasedRestriction,
} from '../../../../Redux/Slices/Policy/Proxy/proxy';
import { RootState } from '../../../../Redux/store';
import { ResponsiveTypography } from '../../../../styles/inputLabel';
import { StyledTextField } from '../../../../styles/textField';
import { SubLabel } from '../../../../styles/sublabel';

function Restriction() {
  const dispatch = useDispatch();
  const proxyInputDetails = useSelector(
    (state: RootState) => state.proxySettings,
  );
  const countriesString = "Afghanistan,Albania,Algeria,American Samoa,Andorra,Angola,Anguilla,Antarctica,Antigua And Barbuda,Argentina,Armenia,Aruba,Australia,Austria,Azerbaijan,Bahamas,Bahrain,Bangladesh,Barbados,Belarus,Belgium,Belize,Benin,Bermuda,Bhutan,Bolivia,Bosnia And Herzegovina,Botswana,Bouvet Island,Brazil,British Indian Ocean Territory,Brunei Darussalam,Bulgaria,Burkina Faso,Burundi,Cambodia,Cameroon,Canada,Cape Verde,Cayman Islands,Central African Republic,Chad,Chile,China,Christmas Island,Cocos (Keeling) Islands,Colombia,Comoros,Democratic People's Republic of Korea,Democratic Republic Of The Congo,Cook Islands,Costa Rica,Cote D'ivoire,Croatia,Cuba,Cyprus,Czech Republic,Denmark,Djibouti,Dominica,Dominican Republic,East Timor,Ecuador,Egypt,El Salvador,Equatorial Guinea,Eritrea,Estonia,Ethiopia,Falkland Islands,Faroe Islands,Fiji,Finland,France,French Guiana,French Polynesia,French Southern Territories,Gabon,Gambia,Georgia,Germany,Ghana,Gibraltar,Greece,Greenland,Grenada,Guadeloupe,Guam,Guatemala,Guinea,Guinea-bissau,Guyana,Haiti,Heard Island And Mcdonald Islands,Holy See (Vatican City State),Honduras,Hong Kong,Hungary,Iceland,India,Indonesia,Iran,Iraq,Ireland,Israel,Italy,Jamaica,Japan,Jordan,Kazakstan,Kenya,Kiribati,Kosovo,Kuwait,Kyrgyzstan,Laos,Latvia,Lebanon,Lesotho,Liberia,Libyan Arab Jamahiriya,Liechtenstein,Lithuania,Luxembourg,Macau,Macedonia,Madagascar,Malawi,Malaysia,Maldives,Mali,Malta,Marshall Islands,Martinique,Mauritania,Mauritius,Mayotte,Mexico,Federated States Of Micronesia,Republic Of Moldova,Monaco,Mongolia,Montserrat,Montenegro,Morocco,Mozambique,Myanmar,Namibia,Nauru,Nepal,Netherlands,Netherlands Antilles,New Caledonia,New Zealand,Nicaragua,Niger,Nigeria,Niue,Norfolk Island,Northern Mariana Islands,Norway,Oman,Pakistan,Palau,Palestinian Territory,Panama,Papua New Guinea,Paraguay,Peru,Philippines,Pitcairn,Poland,Portugal,Puerto Rico,Qatar,Romania,Russia,Rwanda,Saint Helena,Saint Kitts And Nevis,Saint Lucia,Saint Pierre And Miquelon,Saint Vincent And The Grenadines,Samoa,San Marino,Sao Tome And Principe,Saudi Arabia,Senegal,Serbia,Seychelles,Sierra Leone,Singapore,Slovakia,Slovenia,Solomon Islands,Somalia,South Africa,South Georgia And The South Sandwich Islands,The Republic of South Korea,Spain,Sri Lanka,Sudan,Suriname,Svalbard And Jan Mayen,Swaziland,Sweden,Switzerland,Syrian Arab Republic,Taiwan,Tajikistan,Tanzania,Thailand,Togo,Tokelau,Tonga,Trinidad And Tobago,Tunisia,Turkey,Turkmenistan,Turks And Caicos Islands,Tuvalu,Uganda,Ukraine,United Arab Emirates,United Kingdom,United States of America,United States,Uruguay,Uzbekistan,Vanuatu,Venezuela,Vietnam,British Virgin Islands,US Virgin Islands,Wallis And Futuna,Western Sahara,Yemen,Zambia,Zimbabwe";
  const countriesArray = countriesString.split(',');
  const [IPList, setIPList] = useState<string[]>(
    proxyInputDetails.IPRestriction.IPList || [],
  );

  const addIPAddress = () => {
    setIPList([...IPList, '']);
  };

  const updateReduxState = (updatedIPList: string[]) => {
    dispatch(
      updateIPRestriction({
        ...proxyInputDetails.IPRestriction,
        IPList: updatedIPList,
      }),
    );
  };

  const removeIPAddress = (index: number) => {
    const updatedIPList = [...IPList];
    updatedIPList.splice(index, 1);
    setIPList(updatedIPList);
    updateReduxState(updatedIPList);
  };

  const handleChange = (index: number, value: string) => {
    const updatedIPList = [...IPList];
    updatedIPList[index] = value;
    setIPList(updatedIPList);
    updateReduxState(updatedIPList);
  };

  const handleIPRestrictionToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(
      updateIPRestriction({
        ...proxyInputDetails.IPRestriction,
        enable: event.target.checked,
      }),
    );
  };

  const handleLocationRestrictionToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(
      updateLocationBasedRestriction({
        ...proxyInputDetails.LocationBasedRestriction,
        enable: event.target.checked,
      }),
    );
  };

  const handleLocationBasedInput = (
    event: React.ChangeEvent<{}>,
    values: string[],
  ) => {
    dispatch(
      updateLocationBasedRestriction({
        ...proxyInputDetails.LocationBasedRestriction,
        LocationList: values,
      }),
    );
  };

  const handleRestrictIPs = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updateIPRestriction({
        ...proxyInputDetails.IPRestriction,
        restrictIPs: event.target.checked,
      }),
    );
  };

  const handleRestrictLocations = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(
      updateLocationBasedRestriction({
        ...proxyInputDetails.LocationBasedRestriction,
        restrictLocations: event.target.checked,
      }),
    );
  };

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      sx={{ gap: '0.5rem' }}
    >
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        sx={{ gap: '0.5rem' }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <ResponsiveTypography>IP Restriction</ResponsiveTypography>
          <Switch
            name="IPRestrictionToggle"
            checked={proxyInputDetails?.IPRestriction?.enable}
            onChange={handleIPRestrictionToggle}
          />
        </Box>
        <SubLabel>
          IP based restriction is used to either restrict access from or allow
          access to specific IP addresses that are submitted by a user.
          'Restrict' or 'Allow' depends on the user's selection of the toggle
          switch.
        </SubLabel>

        {proxyInputDetails?.IPRestriction?.enable && (
          <>
            <Box display="flex" alignItems="center" sx={{ gap: '4rem' }}>
              <ResponsiveTypography>Restrict below IPs</ResponsiveTypography>
              <Switch
                name="RestrictIPs"
                checked={proxyInputDetails?.IPRestriction?.restrictIPs}
                onChange={handleRestrictIPs}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap="0.75rem">
              <Box>
                <Button onClick={addIPAddress}>+ Add IP Address</Button>
              </Box>
              <Box display="flex" flexDirection="column">
                {IPList.map((ip: string, index: number) => (
                  <Box
                    display="flex"
                    sx={{ gap: '0.5rem', padding: '0.25rem' }}
                  >
                    <StyledTextField
                      id="outlined-basic"
                      size="small"
                      label="IP Address e.g(23.20.000)"
                      variant="outlined"
                      sx={{
                        backgroundColor: '#F6F6F6',
                        border: 'none !important',
                        width: '12.5rem',
                      }}
                      inputProps={{
                        style: {
                          fontSize: '0.938rem',
                        },
                      }}
                      value={ip || ''}
                      onChange={(event: any) => handleChange(index, event.target.value)}
                    />
                    <RemoveCircleIcon
                      fontSize="large"
                      sx={{ color: '#E34614', marginLeft: '2rem' }}
                      onClick={() => removeIPAddress(index)}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        sx={{ gap: '0.5rem' }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <ResponsiveTypography>
            Location Based Restriction
          </ResponsiveTypography>
          <Switch
            checked={proxyInputDetails?.LocationBasedRestriction?.enable}
            onChange={handleLocationRestrictionToggle}
          />
        </Box>
        <SubLabel>
          Location based restriction is used to either restrict access from or
          allow access to specific users from the country that are submitted by
          a user. 'Restrict' or 'Allow' depends on the user's selection of the
          toggle switch.
        </SubLabel>
        {proxyInputDetails?.LocationBasedRestriction?.enable && (
          <>
            <Box display="flex" alignItems="center" sx={{ gap: '4rem' }}>
              <ResponsiveTypography>
                Restrict below Locations
              </ResponsiveTypography>
              <Switch
                name="RestrictLocations"
                checked={
                  proxyInputDetails?.LocationBasedRestriction?.restrictLocations
                }
                onChange={handleRestrictLocations}
              />
            </Box>
            <Autocomplete
              disablePortal
              fullWidth
              size="small"
              id="timezone"
              multiple
              onChange={handleLocationBasedInput}
              options={countriesArray}
              value={proxyInputDetails?.LocationBasedRestriction?.LocationList}
              renderInput={(params) => (
                <StyledTextField {...params} label="Select Location" />
              )}
            />
          </>
        )}
        <br />
      </Box>
    </Box>
  );
}

export default Restriction;
